import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  X,
  TableCellsMerge,
  Sheet,
  FileSliders,
  ChartNoAxesCombined,
  GitGraph,
  Wrench
} from "lucide-react";
import AssetDetails from "./AssetDetails";
import ResultsContent from "./ResultContent";
import ViewLineage from "./ViewLineage";
import ContractDetailsContent from "./ContractDetailsContent";
import LineageTransformationContent from "./LineageTransformationContent";
import heartLoading from "../../../assests/heartLoading.gif";
import { UsageTrend } from "../Domain/UsageTrend";

const AssetDetailsModal = ({
  showAssetModal,
  selectedAssetForView,
  assetLoading,
  assetData,
  setShowAssetModal,
  setSelectedAssetForView,
  setAssetData,
  searchQuery
}) => {
  const [activeTab, setActiveTab] = useState("Data Table");

  const [profilingResults, setProfilingResults] = useState(null);
  const [profilingLoading, setProfilingLoading] = useState(false);
  const [dqResults, setDqResults] = useState(null);
  const [dqLoading, setDqLoading] = useState(false);
  const [activeResultTab, setActiveResultTab] = useState("profile");

  const [contractDetails, setContractDetails] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [usageTrendData, setUsageTrendData] = useState(null);
  const [usageTrendLoading, setUsageTrendLoading] = useState(false);


  const fetchContractDetails = async (dataAssetId) => {
    if (!dataAssetId) return null;
    try {
      setContractLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/view-contracts/${dataAssetId}`
      );
      if (response.data && response.data.status === "success") {
        setContractDetails(response.data.data);
        return response.data.data;
      } else {
        setContractDetails(null);
        return null;
      }
    } catch (err) {
      console.error("Error fetching contract details:", err);
      setContractDetails(null);
      return null;
    } finally {
      setContractLoading(false);
    }
  };

  const fetchProfilingAndDQResults = async () => {
    if (!selectedAssetForView) return;
    const productId = selectedAssetForView.productId;
    const dataAssetId = selectedAssetForView.id;
    if (!productId) return;

    setProfilingLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/profiling_results/${productId}`
      );
      if (response.data && response.data.profiling_results) {
        setProfilingResults(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch profiling results:", err);
    } finally {
      setProfilingLoading(false);
    }

    if (dataAssetId) {
      setDqLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/runQuality/ruleStatus/${productId}`
        );
        if (response.data && Array.isArray(response.data)) {
          const matchingAsset = response.data.find(
            (asset) => asset.data_asset_id === dataAssetId
          );
          if (matchingAsset) setDqResults(matchingAsset);
        }
      } catch (err) {
        console.error("Failed to fetch DQ results:", err);
      } finally {
        setDqLoading(false);
      }
    }
  };

  useEffect(() => {
    if (showAssetModal && selectedAssetForView) {
      if ((activeTab === "Profiling Result" || activeTab === "DQResult") && !profilingResults && !dqResults) {
        fetchProfilingAndDQResults();
      }
      if (activeTab === "Data Configuration" && !contractDetails) {
        fetchContractDetails(selectedAssetForView.id);
      }
    }
  }, [showAssetModal, selectedAssetForView, activeTab]);

  useEffect(() => {
    if (activeTab === "Usage Trend") {
      const fetchUsageTrend = async () => {
        try {
          setUsageTrendLoading(true);
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/data_insights/${selectedAssetForView.id}`
          );
          if (response.status === 200) {
            setUsageTrendData(response.data.graph_data);
          }
        } catch (err) {
          setUsageTrendData({});
        } finally {
          setUsageTrendLoading(false);
        }
      };
      fetchUsageTrend();
    }
  }, [activeTab]);

  if (!showAssetModal) return null;




  const handleClose = () => {
    setShowAssetModal(false);
    setSelectedAssetForView(null);
    setAssetData(null);
    setActiveTab("Data Table");
    setProfilingResults(null);
    setDqResults(null);
    setContractDetails(null);
    setActiveResultTab("profile");
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "Profiling Result" && selectedAssetForView && !profilingResults) {
      fetchProfilingAndDQResults();
    }
    if (tabId === "Data Configuration" && selectedAssetForView && !contractDetails) {
      fetchContractDetails(selectedAssetForView.id);
    }
  };

  const tabs = [
    { id: "Data Table", label: "Assets Table", icon: TableCellsMerge },
    { id: "Data Configuration", label: "Data Contract", icon: Sheet },
    { id: "Profiling Result", label: "Profiling Result", icon: FileSliders },
    { id: "DQResult", label: "Data Quality Result", icon: FileSliders },
    { id: "Remediation Actions", label: "Remediation Actions", icon: Wrench },
    { id: "Technical Lineage", label: "Technical Lineage", icon: GitGraph },
    { id: "Transformation Lineage", label: "Transformation Lineage", icon: GitGraph },
    { id: "Usage Trend", label: "Usage Trend", icon: ChartNoAxesCombined },
  ];

  return (
    <div
      className="fixed  inset-0 w-[100%] h-[100%] bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg w-[1230px] h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header - always visible */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Asset Details</h2>
            {selectedAssetForView && (
              <p className="text-sm text-gray-600 mt-1">
                {selectedAssetForView.name}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs - always visible */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-8 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`whitespace-nowrap flex-shrink-0 flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? "border-[#bb1ccc] text-[#bb1ccc]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">

          {/* Loader inside content area */}
          {assetLoading && (
            <div className="flex items-center justify-center h-[300px]">
              <div className="flex flex-col items-center space-y-3">
                <img src={heartLoading} alt="Loading..." className="h-30" />
                <span className="text-gray-700 text-[20px] text-center">
                  Loading asset details...
                </span>
              </div>
            </div>
          )}

          {!assetLoading && activeTab === "Data Table" && (
            <AssetDetails assetData={assetData} searchQuery={searchQuery} />
          )}

          {!assetLoading && activeTab === "Data Configuration" && (
            <ContractDetailsContent
              selectedContractForView={selectedAssetForView}
              contractDetails={contractDetails}
              contractLoading={contractLoading}
            />
          )}

          {!assetLoading && activeTab === "Profiling Result" && (
            <ResultsContent
              activeResultTab={activeResultTab}
              setActiveResultTab={setActiveResultTab}
              profilingLoading={profilingLoading}
              profilingResults={profilingResults}
              dqLoading={dqLoading}
              dqResults={dqResults}
              assetName={selectedAssetForView?.name}
              showTabs={false}
            />
          )}

          {!assetLoading && activeTab === "DQResult" && (
            <ResultsContent
              activeResultTab="quality"
              setActiveResultTab={setActiveResultTab}
              profilingLoading={false}
              profilingResults={null}
              dqLoading={dqLoading}
              dqResults={dqResults}
              assetName={selectedAssetForView?.name}
              showTabs={false}
            />
          )}
          {!assetLoading && activeTab === "Remediation Actions" && (
            <div className="flex flex-col items-center justify-center h-[300px] gap-2 text-gray-400">
              <h3 className="text-lg font-semibold">Coming Soon</h3>
              <p className="text-sm">Remediation Actions will be available shortly.</p>
            </div>
          )}
          {!assetLoading && activeTab === "Technical Lineage" && (
            <div className="h-[calc(90vh-300px)]">
              <ViewLineage contractData={selectedAssetForView} />
            </div>
          )}

          {!assetLoading && activeTab === "Transformation Lineage" && (
            <LineageTransformationContent assetName={selectedAssetForView?.name} />
          )}

          {!assetLoading && activeTab === "Usage Trend" && (
            <UsageTrend graphData={usageTrendData} loading={usageTrendLoading} />

          )}

        </div>

      </div>
    </div>
  );
};

export default AssetDetailsModal;